import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Alert,
  Button,
  Field,
  Input,
  Select,
  Spinner,
} from "../../design-system";
import { AuthShell } from "../../design-system/layout/AuthShell";
import { bootstrapFirstWorkspace, resetRegisterFlag } from "../../slices/thunks";
import { getLoggedinUser } from "../../helpers/api_helper";
import { TIMEZONES, localTimezone } from "../../helpers/timezones";

const selectState = createSelector(
  (state) => state.Account,
  (account) => ({
    loading: account.loading,
    error: account.registrationError,
  }),
);

const RegisterWorkspace = () => {
  document.title = "Registrar empresa | Kronaly";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector(selectState);

  useEffect(() => {
    if (!getLoggedinUser()?.access_token) {
      navigate("/login", { replace: true });
      return;
    }
    dispatch(resetRegisterFlag());
  }, [dispatch, navigate]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { name: "", country: "", timezone: localTimezone() || "Europe/Madrid" },
    validationSchema: Yup.object({
      name: Yup.string().required("Razón social obligatoria").max(255),
      country: Yup.string().required("País obligatorio").max(120),
      timezone: Yup.string().required("Zona horaria obligatoria").max(120),
    }),
    onSubmit: (values) => {
      dispatch(bootstrapFirstWorkspace(values, navigate));
    },
  });

  const fieldError = (k) =>
    validation.touched[k] && validation.errors[k] ? validation.errors[k] : null;

  return (
    <AuthShell
      footer={
        <Link to="/logout" style={{ color: "var(--kr-fg-link)", fontWeight: 600 }}>
          Cerrar sesión
        </Link>
      }
    >
      <div className="kr-mb-4">
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
          Registra tu empresa
        </h1>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--kr-fg-muted)" }}>
          Estos datos crean tu primer workspace en Kronaly.
        </p>
      </div>

      {error && <div className="kr-mb-3"><Alert tone="danger">{error}</Alert></div>}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
        }}
        className="kr-flex kr-col kr-gap-4"
        noValidate
      >
        <Field label="Razón social" required error={fieldError("name")}>
          <Input
            type="text"
            name="name"
            value={validation.values.name}
            onChange={validation.handleChange}
            placeholder="Razón social de la empresa"
            inputProps={{ onBlur: validation.handleBlur }}
            autoFocus
          />
        </Field>
        <Field label="País" required error={fieldError("country")}>
          <Input
            type="text"
            name="country"
            value={validation.values.country}
            onChange={validation.handleChange}
            placeholder="Ej. España"
            inputProps={{ onBlur: validation.handleBlur }}
          />
        </Field>
        <Field label="Zona horaria" required error={fieldError("timezone")}>
          <Select
            name="timezone"
            value={validation.values.timezone}
            onChange={validation.handleChange}
          >
            <option value="">— Selecciona zona horaria —</option>
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </Select>
        </Field>
        <Button variant="primary" size="lg" type="submit" disabled={loading} fullWidth>
          {loading && <Spinner size={14} color="#fff" />}
          {loading ? "Creando empresa…" : "Continuar"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default RegisterWorkspace;
